import { useState } from 'react';
import { Pressable, useColorScheme } from 'react-native';
import {
  Check,
  LayoutGrid,
  MapPin,
  Megaphone,
  MessageCircle,
  Palette,
  Star,
  ThumbsUp,
  Users,
  type LucideIcon,
} from 'lucide-react-native';
import { XStack } from 'tamagui';

import { Text } from '@/components/text';
import type { ThreadCategory } from '@/lib/api';
import { hit, slopFor } from '@/lib/hit-slop';
import { borderWidth, darkColors, lightColors, radius, space } from '@/theme/tokens.generated';

// Decision 8 (issue #569): one icon per category, replacing the six
// OKLCH hues. On a muted neutral chip the icon is the at-a-glance
// differentiator (the old color-coding job), and it survives
// color-blindness and dark mode without a second signal. "All"
// (category undefined) gets LayoutGrid so the filter row keeps one
// rhythm.
const CATEGORY_ICONS: Record<ThreadCategory, LucideIcon> = {
  local: MapPin,
  hobby: Palette,
  pet_peeve: Megaphone,
  meetup: Users,
  recommendation: ThumbsUp,
  review: Star,
  chat: MessageCircle,
};

export const CHIP_HEIGHT = 36;

// The one interactive filter shape: 36 high, pill, 16px icon + 14/500 label.
// Selected is a tint, a 2px brand border AND a check glyph replacing the icon,
// so selection never rests on colour alone. The 44px hit area comes from
// hitSlop, not from the visual height. Static labels are never chips: see
// Metadata (plain text + icon) and Badge (rounded rect).
export function Chip({
  label,
  icon: Icon,
  selected,
  onPress,
}: {
  label: string;
  icon?: LucideIcon;
  selected?: boolean;
  onPress: () => void;
}) {
  const [focused, setFocused] = useState(false);
  const dark = useColorScheme() === 'dark';
  const colors = dark ? darkColors : lightColors;
  const Glyph = selected ? Check : Icon;
  // The selected 2px border grows inward, so padding gives back the extra
  // pixel and the chip never changes width.
  const bw = selected ? borderWidth.control : borderWidth.hairline;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: !!selected }}
      aria-pressed={!!selected}
      {...hit({ vertical: slopFor(CHIP_HEIGHT) })}
      onPress={onPress}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={({ pressed }) => [
        { alignSelf: 'flex-start' as const, borderRadius: radius.full },
        focused
          ? {
              outlineStyle: 'solid' as const,
              outlineWidth: 2,
              outlineColor: colors.actionPrimary,
              outlineOffset: 2,
            }
          : null,
        pressed ? { opacity: 0.7 } : null,
      ]}
    >
      <XStack
        height={CHIP_HEIGHT}
        alignItems="center"
        gap="$2"
        paddingHorizontal={space[3] - (bw - borderWidth.hairline)}
        borderRadius="$full"
        backgroundColor={selected ? colors.interactiveSelectedBg : colors.badgeNeutralBg}
        borderWidth={bw}
        borderColor={selected ? colors.actionPrimary : colors.borderStrong}
      >
        {Glyph && <Glyph size={16} strokeWidth={1.75} color={colors.badgeNeutralText} />}
        <Text color="$badgeNeutralText" fontSize="$meta" lineHeight="$meta" fontWeight="$medium" textAlign="left">
          {label}
        </Text>
      </XStack>
    </Pressable>
  );
}

export function CategoryChip({
  label,
  category,
  selected,
  onPress,
}: {
  label: string;
  category?: ThreadCategory;
  selected?: boolean;
  onPress: () => void;
}) {
  return (
    <Chip label={label} icon={category ? CATEGORY_ICONS[category] : LayoutGrid} selected={selected} onPress={onPress} />
  );
}

export { CATEGORY_ICONS };
